// ./src/utils/api/RsvpQuestionOptionService.ts
//
// Choices for a choice-type registration question. The question editor syncs
// these (create/update/remove) to match the edited option list on save.
import { gql } from '@apollo/client';
import { OpResult } from '@sdflc/api-helpers';

import { ApiService } from './ApiService';
import { RsvpQuestionOption } from './RsvpQuestionService';

const FIELDS = `
  id
  rsvpQuestionId
  label
  optionValue
  description
  price
  isDefault
  orderNo
  status
`;

const LIST_QUERY = gql`
  query RsvpQuestionOptionList($filter: RsvpQuestionOptionFilter, $params: PaginationAndSorting) {
    rsvpQuestionOptionList(filter: $filter, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation RsvpQuestionOptionCreate($params: RsvpQuestionOptionInput) {
    rsvpQuestionOptionCreate(params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation RsvpQuestionOptionUpdate($where: RsvpQuestionOptionWhereInput, $params: RsvpQuestionOptionInput) {
    rsvpQuestionOptionUpdate(where: $where, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation RsvpQuestionOptionRemove($where: RsvpQuestionOptionWhereInput) {
    rsvpQuestionOptionRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

export interface RsvpQuestionOptionInput {
  rsvpQuestionId?: string | null;
  label?: string | null;
  optionValue?: string | null;
  description?: string | null;
  price?: number | null;
  isDefault?: boolean | null;
  orderNo?: number | null;
}

export class RsvpQuestionOptionService {
  static async list(rsvpQuestionId: string): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, { filter: { rsvpQuestionId: [rsvpQuestionId] } });
  }

  static async create(params: RsvpQuestionOptionInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  static async update(id: string, params: RsvpQuestionOptionInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id }, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }
}

export type { RsvpQuestionOption };
